import React, {useRef, useState, useEffect, useCallback} from 'react';
import { ReactReduxContext, Provider } from 'react-redux';
import {useZoom} from './useZoom.js';
import {crop} from 'engine/Crop.js';
import ColoredWaveformPoints from './ColoredWaveformPoints.js';
import WaveformPoints from './WaveformPoints.js';
import {convertSecondsToSpace, convertSpaceToSeconds} from './convertSecondsToSpace.js';
import LoopPoints from './LoopPoints.js';
import { connect } from "react-redux";
import PlayTick from './PlayTick.js';
import { Group, Stage, Layer, FastLayer, Rect, Line} from 'react-konva';
import {convertPointsToList} from './convertPointsToList.js';
import WaveformCache from 'repos/WaveformCache.js';
import Colors from 'components/Colors.js';
import {getWidth, WIDTH, HEIGHT} from './constants.js';

const colors = new Colors();
var waveformCache = new WaveformCache(445, 75);

export const PrettyWaveform = (props) => {
    let backgroundColor = props.backgroundColor || 'black';
    const {buffer, soundId, instrument, sampler } = props;
    const [draggingStart, setDraggingStart] = useState(false);
    const [draggingEnd, setDraggingEnd] = useState(false);
    const [width, setWidth] = useState(props.width || WIDTH);
    const [height, setHeight] = useState(props.height);
    const [waveformPoints, setWaveformPoints] = useState([]);
    const [coloredWaveformPoints, setColoredWaveformPoints] = useState([]);
    const [rmsPoints, setRmsPoints] = useState([]);

    if (colors.getBrightness(props.color) < 110) {
        backgroundColor = colors.brighten(backgroundColor, 2);
    }
    // zoom variables
    const [hovered, setHovered] = useState(false);
    const mouseX = useRef(0);
    const ref = useRef(null);
    const {
        zoomedBuffer,
        zoomedRmsPoints,
        zoomedColoredPoints,
        zoomedWaveformPoints,
        zoomedStartTime} = useZoom(buffer, hovered, mouseX, width, waveformCache, sampler);

    useEffect(() => {
        if (!buffer) {
            return;
        }
        let width = props.width || getWidth(props.div);
        let height = props.height || HEIGHT;
        sampler.showingPrettyWaveform = true;
        setWidth(width);
        setHeight(height);
        if (height !== waveformCache.height ||
            width !== waveformCache.width) {
            waveformCache = new WaveformCache(width, height);
        }
        if (buffer.duration < 3) {
            waveformCache.getBasicWaveform( soundId, buffer)
                .then((pts) => {
                    pts.sort((a,b) => a.x - b.x);
                    setRmsPoints(convertPointsToList(pts, 'rmsY'));
                    setWaveformPoints(convertPointsToList(pts, 'y'));
                    setColoredWaveformPoints([]);
                });
        } else {
            waveformCache.getWaveform(soundId, buffer)
                .then((pts) => {
                    setRmsPoints([]);
                    setWaveformPoints([]);
                    setColoredWaveformPoints(pts);
                });
        }
        return () => {
            sampler.showingPrettyWaveform = false;
        };
    }, [
        setWidth,
        setHeight,
        setRmsPoints,
        setWaveformPoints,
        buffer,
        soundId,
        sampler,
        setColoredWaveformPoints
    ]);
       
    const clearDragging = useCallback(() => {
        setDraggingStart(false);
        setDraggingEnd(false);
    }, [setDraggingStart, setDraggingEnd]);

    const onMouseLeave = useCallback(() => {
        setHovered(false);
        clearDragging();
    }, [setHovered]);

    const onMouseMove = useCallback((e) => {
        let bounding = ref.current.getBoundingClientRect();
        let x = e.clientX - bounding.left;
        mouseX.current = x;
        let percentage = 100.0 * x / width;
        if (zoomedBuffer) {
            // percentage of zoomed start time
            let total = zoomedStartTime + zoomedBuffer.duration * percentage / 100;
            percentage = 100 * total / buffer.duration;

        }
        if (draggingStart) {
            instrument.loopStartParameter.set(Math.max(0, percentage), false);
        }

        if (draggingEnd) {
            instrument.loopLengthParameter.set(Math.max(0, percentage - instrument.loopStart), false);
        }
    }, [instrument, draggingStart, draggingEnd,  width, ref, zoomedBuffer, zoomedStartTime]);

    let color = colors.saturate(colors.getColorForHash(soundId), 3);

    return (
        <div
          onMouseOver={x => setHovered(true)}
          onMouseUp={clearDragging}
          onMouseLeave={onMouseLeave}
          onMouseMove={onMouseMove}
          ref={ref}
          className="sound-waveform">
          <Stage
            width={width} height={height}>
            <Provider store={props.store}>
              <Layer
                onMouseUp={clearDragging}
              >
                <Rect width={width} height={height} x={0} y={0} fill={backgroundColor}/>
              </Layer>
              {coloredWaveformPoints.length > 0 ?
               <ColoredWaveformPoints
                 coloredWaveformPoints={zoomedColoredPoints || coloredWaveformPoints}/> :
               <WaveformPoints
                 color={color}
                 rmsPoints={zoomedRmsPoints || rmsPoints}
                 waveformPoints={zoomedWaveformPoints || waveformPoints}/>}
              <LoopPoints
                setDraggingStart={setDraggingStart}
                setDraggingEnd={setDraggingEnd}
                instrument={instrument}
                zoomedStartTime={zoomedStartTime || 0}
                color={color}
                buffer={zoomedBuffer || buffer}
                sampler={sampler}
                width={width}
                height={height}/>
              <FastLayer>
                <PlayTick
                  instrument={instrument}
                  soundId={soundId}
                  height={height}
                  convertSecondsToSpace={
                      (sec) =>
                          convertSecondsToSpace(
                              sec - (zoomedStartTime || 0),
                              zoomedBuffer || buffer,
                              width)}/>
              </FastLayer>
            </Provider>
          </Stage>
        </div>
    );
};

const mapDispatchToProps = (dispatch, props) => {
    return {
    };
};

export default connect(null, mapDispatchToProps)(PrettyWaveform);



